"use client";

import { HeroSection } from "@/components/shared/hero_section";
import { BenefitsSection } from "@/components/shared/benefits_section";
import { EvidenceSection } from "@/components/shared/evidence_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { QuoteCarousel, QuoteData } from "@/components/shared/quote_carousel";
import { useTranslations } from "next-intl";

const FEColleges = () => {
    const t = useTranslations();
    
    // Example quotes data for FE colleges
    const collegeQuotes: QuoteData[] = [
        {
            text: "StudyStash has clearly helped our BTEC and A-Level students with their study and revision. We're excited to continue working with you to further enhance student outcomes",
            author: "Trina Tiernan",
            title: "Learning and Employability Manager, Joseph Chamberlain College",
            image: "/case-studies/Trina_Tiernan.jpeg",
            logo: "/case-studies/JCC_logo.jpg"
        },
    ];

    return (
        <main className="pt-8">
            <div className="flex w-full flex-col items-center">
                <HeroSection 
                    desktopImage="/apple_images.png"
                    mobileImage="/apple_image_mobile.png"
                    desktopImageAlt="StudyStash Dashboard shown on a MacBook Pro, iPad and iPhone"
                    mobileImageAlt="StudyStash Dashboard shown on a MacBook Pro"
                    heading="Always-on study and revision support tools for your college"
                    description="Colleges who only rely on static learning resources face poor engagement and overwhelmed teachers. StudyStash is your scalable teaching and learning support system."
                    buttonHref="/pilot-program-fe-colleges"
                />

                <div className="relative w-full pt-96">
                    <div className="absolute top-[400px] right-0 pointer-events-none hidden lg:block">
                        <img 
                            src="/left-right-lines.svg" 
                            alt="Illustration of the StudyStash teaching-learning loop" 
                            className="w-[300px] h-auto" 
                        />
                    </div>
                    
                    <BenefitsSection 
                        heading="Loved by students and teachers alike"
                        subheading1="StudyStash turns broadcast content (pdfs, powerpoints, docs) into study and revision assets to boost outcomes impact without disruption."
                        subheading2="We support students at scale with safe AI tools they love and use, boosting course engagement while helping teachers and departments deliver better outcomes."
                        cards={[
                            {
                                title: "Students",
                                description: "We transform overwhelming subject content into study assets that build confidence, reduce stress, and boost grades.",
                                image: "/for-students-small.png",
                                imageAlt: "University features",
                                bgColor: "bg-ss-purple"
                            },
                            {
                                title: "Teachers",
                                description: "We reduce teachers’ workload by handling hundreds of student questions and creating course materials.",
                                image: "/for-lecturers-small.png",
                                imageAlt: "Lecturer features",
                                bgColor: "bg-ss-blue"
                            },
                            {
                                title: "Colleges",
                                description: "Support students and teachers and unlock high quality, scalable teaching and learning with our AI-powered tools.",
                                image: "/for-universities-small.png",
                                imageAlt: "Student features",
                                bgColor: "bg-ss-green"
                            }
                        ]}
                        buttonText="See how it works"
                        showButton={true}
                        buttonHref="/book-a-demo"
                    />
                </div>

                <div className="pt-10 w-full">
                    <EvidenceSection 
                        source="Source – Joseph Chamberlain College pilot 2025"
                        heading="Leading-edge learning that delivers"
                        description="When colleges implement StudyStash, they see measurable improvements across key metrics that matter:"
                        circles={[
                            {
                                value: "61%",
                                title: "Student Grades",
                                description: "Of students reported StudyStash helped improve their academic performance"
                            },
                            {
                                value: "57%",
                                title: "Student Confidence",
                                description: "Of students reported increased confidence managing study workload"
                            },
                            {
                                value: "4.8%",
                                title: "Power Students",
                                description: "Achieved a 14+ day streak (avg. use time 2 hours 03 mins/week)"
                            }
                        ]} 
                    />
                </div>

                <div className="relative w-full pt-24">
                    {/* Background SVG for LeftRightSection */}
                    <div className="absolute left-0 top-[300px] w-full h-1/2 pointer-events-none z-0">
                        <img
                            src="/left-right-loops.svg"
                            alt="Illustration of the StudyStash teaching-learning loop"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    
                    <div className="relative z-10">
                        <AlternatingSection 
                            sections={[
                                {
                                    subtitle: "Student outcomes",
                                    title: "Boost completion rates",
                                    content: (
                                        <div>
                                            <p>Our platform helps FE colleges improve student retention and achievement through {t("general.words.personalised")} learning support and early intervention tools.</p>
                                            <p>Track student progress in real-time and identify those who need additional support before they fall behind.</p>
                                        </div>
                                    ),
                                    image: {
                                        src: "/college_photo.jpg",
                                        alt: "Demonstration of student achievement tracking",
                                    }
                                },
                                {
                                    subtitle: "Vocational focus",
                                    title: "Support practical learning",
                                    content: (
                                        <div>
                                            <p>StudyStash adapts to both academic and vocational learning styles, supporting students across all types of qualifications and courses.</p>
                                            <p>Our platform helps students master both theoretical knowledge and practical skills through interactive learning tools and assessments.</p>
                                        </div>
                                    ),
                                    image: {
                                        src: "/in-class.jpg",
                                        alt: "Demonstration of vocational learning support",
                                    }
                                }
                            ]}
                        />
                    </div>
                </div>

                <div className="pb-24 -pt-8 container mx-auto px-6 lg:px-8">
                    <QuoteCarousel quotes={collegeQuotes} />
                </div>

            </div>
        </main>
    );
};

export default FEColleges;
